// Magic-link interstitial for CGC member sign-in (DESIGN §5.5). Mirror of
// [`AdminVerify`](./AdminVerify.tsx) — see that file for the prefetch-defence
// rationale. CGC members are external clinical/legal advisers whose corp
// mailboxes are the most likely to sit behind aggressive URL scanners.

import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Logo } from '@/components/brand/Logo.tsx';
import { api, ApiError } from '@/lib/api.ts';
import { Button } from '@/components/ui/Button.tsx';

interface CgcVerifyResponse {
  stage: 'enroll' | 'verify';
  cid: string;
}

export function CgcVerify() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  // Capture once at mount, then strip from the URL bar — see AdminVerify for rationale.
  const [token] = useState(() => params.get('token') ?? '');
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (url.searchParams.has('token')) {
      url.searchParams.delete('token');
      window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
    }
  }, []);

  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onContinue() {
    setError(null);
    setPending(true);
    try {
      const res = await api.post<CgcVerifyResponse>('/auth/cgc/verify', { token });
      await navigate(
        `/cgc/totp?stage=${encodeURIComponent(res.stage)}&cid=${encodeURIComponent(res.cid)}`,
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError('This link has expired or has already been used. Request a new sign-in link.');
      } else if (err instanceof ApiError && err.status === 400) {
        setError('This link is malformed. Request a new sign-in link.');
      } else {
        setError('Something went wrong. Try again, or request a new link.');
      }
    } finally {
      setPending(false);
    }
  }

  if (!token) {
    return (
      <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-6 py-12">
        <Logo className="text-2xl" />
        <h1 className="mt-6 text-2xl font-semibold tracking-tight">Sign-in</h1>
        <p className="mt-4 text-sm text-traffic-red" role="alert">
          No token in URL — open the link from your sign-in email.
        </p>
        <p className="mt-4 text-sm">
          <Link className="underline underline-offset-2 hover:no-underline" to="/cgc/sign-in">
            Back to CGC sign-in
          </Link>
        </p>
      </main>
    );
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-6 py-12">
      <Logo className="text-2xl" />
      <h1 className="mt-6 text-2xl font-semibold tracking-tight">Continue CGC sign-in</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Click Continue to redeem your sign-in link. The next step is your authenticator code.
      </p>
      <div className="mt-6">
        <Button onClick={onContinue} loading={pending}>
          Continue
        </Button>
      </div>
      {error ? (
        <p className="mt-4 text-sm text-traffic-red" role="alert">
          {error}{' '}
          <Link className="underline underline-offset-2 hover:no-underline" to="/cgc/sign-in">
            Request a new link
          </Link>
          .
        </p>
      ) : null}
      <p className="mt-6 text-xs text-muted-foreground">
        Single-use link. If your email provider previewed it before you got here, you may need to
        request a new one.
      </p>
    </main>
  );
}
